import { Component, ElementRef, EventEmitter, HostBinding, Input, booleanAttribute, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  CalendarCell,
  CalendarPair,
  DateRangeFilterOption,
  DateRangeFilterRange,
  TypedDateProblem,
  calendarsFor,
  dayOf,
  daysInRange,
  isCompleteTypedDate,
  MAX_YEAR,
  MIN_YEAR,
  maskDateDigits,
  monthCells,
  ordered,
  parseTypedDate,
  placeCalendar,
  rangeDayClasses,
  sameDay,
  splitPresetLabel,
  stepCalendar,
  typedDateProblem,
  yearOptions,
} from './date-range-filter.utils';
import { clearDateRange, saveDateRange } from './date-range-memory';

interface MonthView {
  year: number;
  month: number;
  title: string;
  cells: CalendarCell[];
}

/**
 * Card #213: one date range filter for reports and lists.
 * Presets (the page's own analytics options) apply in one click; a custom
 * range is picked on two side-by-side months with labelled Start / End boxes.
 * The page keeps owning what each preset means - this only reports the choice.
 */
@Component({
  selector: 'app-date-range-filter',
  standalone: true,
  imports: [CommonModule, OverlayModule, TranslateModule],
  templateUrl: './date-range-filter.component.html',
  styleUrls: ['./date-range-filter.component.scss'],
})
export class DateRangeFilterComponent implements OnChanges, OnDestroy {
  /** Preset options, e.g. { value: 'Monthly analytics', label: 'Monthly analytics (Sep 1, 2026 to Sep 30, 2026)' }. */
  @Input() options: DateRangeFilterOption[] = [];
  /** The selected option value, or customValue when a custom range is active. */
  @Input() value = '';
  @Input() customValue = 'custom_range';
  /** The active custom range, when value === customValue. */
  @Input() start: Date | null = null;
  @Input() end: Date | null = null;
  /** Text before the selection on the button, e.g. "Show". Already translated. */
  @Input() prefix = '';
  /** First column of the calendar: 0 = Sunday .. 6 = Saturday. */
  @Input() weekStartsOn = 1;

  @Output() presetChange = new EventEmitter<string>();
  @Output() customRangeChange = new EventEmitter<DateRangeFilterRange>();
  /** Show a calendar icon instead of the labelled button (compact headers, e.g. Tasks reminders). */
  @Input({ transform: booleanAttribute }) iconOnly = false;
  /** Offer a "Clear" button while a custom range is applied; emits cleared. */
  @Input({ transform: booleanAttribute }) clearable = false;
  @Output() cleared = new EventEmitter<void>();
  /**
   * Card #269: remember an applied custom range for this screen (see date-range-memory.ts).
   * Apply saves it; a preset or Clear forgets it. The page restores it on load.
   * Also shows the Clear control while a custom range is applied.
   */
  @Input() persistKey = '';
  /**
   * Card #269: the preset Clear returns to (the screen's default). When set, Clear emits
   * presetChange(resetValue) INSTEAD of cleared, so a page never reloads twice for one Clear.
   */
  @Input() resetValue = '';

  open = false;
  today = dayOf(new Date());
  draftStart: Date | null = null;
  draftEnd: Date | null = null;
  /** Which box the next calendar click fills. */
  setting: 'start' | 'end' = 'start';
  hover: Date | null = null;
  /** The left and right calendars' months. They move independently (card #213). */
  calendars: CalendarPair = [{ year: 0, month: 0 }, { year: 0, month: 1 }];
  months: MonthView[] = [];
  weekdays: string[] = [];
  /** Month names and years for the jump-to selects on each month (card #219). */
  monthLabels: string[] = [];
  years: number[] = [];
  startText = '';
  endText = '';
  /**
   * Each box on its own (card #213): 'ok' shows the date Apply will use, 'editing'
   * is part-typed, 'bad' cannot be a date. Apply needs both boxes 'ok', so it never
   * applies something other than what the boxes say - even with both boxes wrong.
   */
  box: Record<'start' | 'end', 'ok' | 'editing' | 'bad'> = { start: 'ok', end: 'ok' };
  /** What is wrong with a 'bad' box, already in words (null: not recognised). */
  private problemTexts: Record<'start' | 'end', string | null> = { start: null, end: null };
  @ViewChild('startBox') private startBox?: ElementRef<HTMLInputElement>;
  @ViewChild('endBox') private endBoxRef?: ElementRef<HTMLInputElement>;
  private dateFormat: Intl.DateTimeFormat | null = null;
  /** What typing last applied in each box, so leaving the box doesn't apply it twice. */
  private appliedText: Record<'start' | 'end', string> = { start: '', end: '' };
  private focusTimer: ReturnType<typeof setTimeout> | null = null;
  /** Counts typing events, so a scheduled jump to End can tell it has gone stale. */
  private keystrokes = 0;
  private dateFormatLang = '';
  private langChecked = '';
  private langResolved = 'en';

  readonly positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
  ];

  constructor(private translate: TranslateService) {}

  /** The week start can arrive after the panel is already open (the profile loads async). */
  ngOnChanges(changes: SimpleChanges): void {
    if (this.open && changes['weekStartsOn']) this.build();
    // The page changed the dates: what typing applied earlier no longer counts.
    if (changes['start'] || changes['end'] || changes['value']) this.appliedText = { start: '', end: '' };
  }

  get presets(): { value: string; name: string; dates: string }[] {
    return (this.options || [])
      .filter(o => o.value !== this.customValue)
      .map(o => ({ value: o.value, ...splitPresetLabel(o.label) }));
  }

  /** The inline Clear sits beside the button (only then is the host laid out as a row). */
  @HostBinding('class.drf-has-reset')
  get showReset(): boolean {
    return !!this.persistKey && this.isCustom && !this.iconOnly;
  }

  /** Clear is offered while a custom range is applied, on pages that ask for it or remember the range. */
  get showClear(): boolean {
    return (this.clearable || !!this.persistKey) && this.isCustom;
  }

  get isCustom(): boolean {
    return this.value === this.customValue && !!this.start && !!this.end;
  }

  /** What the closed button shows. */
  get summary(): { name: string; dates: string } {
    if (this.isCustom) {
      return { name: this.t('DATE_RANGE.CUSTOM', 'Custom'), dates: this.formatRange(this.start!, this.end!) };
    }
    const hit = this.presets.find(p => p.value === this.value);
    return hit ? { name: hit.name, dates: hit.dates } : { name: this.t('DATE_RANGE.CHOOSE', 'Choose dates'), dates: '' };
  }

  /** The first box that cannot be a date, if any. */
  get invalid(): 'start' | 'end' | null {
    return this.box.start === 'bad' ? 'start' : this.box.end === 'bad' ? 'end' : null;
  }

  /** Both boxes must show the dates that will be applied. */
  get canApply(): boolean {
    return !!this.draftStart && !!this.draftEnd && this.box.start === 'ok' && this.box.end === 'ok';
  }

  /** The calendars are not side by side: each then gets both arrows, to walk them back together. */
  get apart(): boolean {
    const [a, b] = this.calendars;
    return b.year * 12 + b.month - (a.year * 12 + a.month) > 1;
  }

  get hint(): string {
    const bad = this.invalid;
    if (bad) {
      return this.problemTexts[bad] ?? this.t('DATE_RANGE.INVALID', 'That date was not recognised. Try 03/08/2026 or Aug 3, 2026.');
    }
    if (this.box.start === 'editing' || this.box.end === 'editing') {
      return this.t('DATE_RANGE.FINISH_TYPING', 'Finish typing the date (dd/mm/yyyy), or pick it on the calendar.');
    }
    // Only ask for an end date when there isn't one yet.
    if (this.setting === 'end' && this.draftStart && !this.draftEnd) {
      return this.t('DATE_RANGE.PICK_END', 'Start is {{date}}. Now pick an end date.', { date: this.formatDate(this.draftStart) });
    }
    if (this.canApply) {
      // Both dates chosen: say so, rather than asking for a start date again.
      return this.t('DATE_RANGE.READY', 'Range ready. Press Apply, or click a day to start a new range.');
    }
    return this.t('DATE_RANGE.PICK_START', 'Pick a start date, or choose a quick range.');
  }

  get spanText(): string {
    if (!this.canApply) return this.t('DATE_RANGE.CHOOSE_END', 'Choose an end date');
    const n = daysInRange(this.draftStart!, this.draftEnd!);
    return `${this.formatRange(this.draftStart!, this.draftEnd!)} · ${this.t('DATE_RANGE.DAYS', '{{count}} days', { count: n })}`;
  }

  toggle(): void {
    this.open ? this.close() : this.openPanel();
  }

  openPanel(): void {
    this.today = dayOf(new Date());
    this.draftStart = this.isCustom ? dayOf(this.start!) : null;
    this.draftEnd = this.isCustom ? dayOf(this.end!) : null;
    this.setting = 'start';
    this.hover = null;
    this.resetBoxes();
    this.appliedText = { start: '', end: '' };
    // Last month + this month, or the start's and end's months of the active range.
    this.calendars = calendarsFor(this.draftStart, this.draftEnd, this.today);
    this.syncText();
    this.build();
    this.open = true;
  }

  /** A click on the button itself is handled by toggle(); closing here too would reopen it. */
  outsideClick(event: MouseEvent, trigger: HTMLElement): void {
    if (trigger.contains(event.target as Node)) return;
    this.close();
  }

  close(): void {
    this.open = false;
    this.hover = null;
    this.clearFocusTimer();
  }

  /** Emits even for the current preset, so picking it again reloads (e.g. after a failed load). */
  choosePreset(value: string): void {
    this.close();
    if (this.persistKey) clearDateRange(this.persistKey);
    this.presetChange.emit(value);
  }

  clear(): void {
    this.close();
    if (this.persistKey) clearDateRange(this.persistKey);
    // Back to the screen's default through the same path as picking that preset; pages
    // without a default (e.g. Tasks reminders) handle `cleared` themselves. Never both.
    if (this.resetValue) this.presetChange.emit(this.resetValue);
    else this.cleared.emit();
  }

  apply(): void {
    if (!this.canApply) return;
    const range = ordered(this.draftStart!, this.draftEnd!);
    this.close();
    if (this.persistKey) saveDateRange(this.persistKey, range);
    this.customRangeChange.emit(range);
  }

  pickDay(d: Date): void {
    this.resetBoxes();
    // A calendar click replaces whatever was typed, so forget what typing applied.
    this.appliedText = { start: '', end: '' };
    if (this.setting === 'start' || !this.draftStart) {
      this.draftStart = d;
      this.draftEnd = null;
      this.setting = 'end';
    } else {
      const r = ordered(this.draftStart, d);
      this.draftStart = r.start;
      this.draftEnd = r.end;
      this.setting = 'start';
    }
    this.hover = null;
    this.syncText();
    // Both boxes are now 'ok', so both must SHOW the dates: a box still holding typed
    // text ("40") whose date didn't change would not be redrawn by the binding.
    this.paintBoxes();
  }

  focusBox(which: 'start' | 'end'): void {
    if (which === 'end' && !this.draftStart) return;
    this.setting = which;
  }

  /**
   * Apply a typed date. moveView = false keeps the calendar where it is: leaving
   * a box is often the first half of a click on a day, and moving the months
   * under the pointer would swallow that click.
   */
  typed(which: 'start' | 'end', text: string, moveView = true): void {
    const d = parseTypedDate(text, [this.monthNames('long'), this.monthNames('short')]);
    if (!d) {
      this.setBox(which, 'bad', typedDateProblem(text)); // a specific reason when there is one
      return;
    }
    const before = { start: this.startText, end: this.endText };
    if (which === 'start') {
      this.draftStart = d;
      if (this.draftEnd && this.draftEnd < d) this.draftEnd = null;
      this.setting = this.draftEnd ? 'start' : 'end';
    } else {
      if (this.draftStart) {
        const r = ordered(this.draftStart, d);
        this.draftStart = r.start;
        this.draftEnd = r.end;
      } else {
        this.draftEnd = d;
      }
      this.setting = 'start';
    }
    // Start's month on the left, end's month on the right, however far apart.
    if (moveView) this.calendars = calendarsFor(this.draftStart, this.draftEnd, this.today);
    this.syncText();
    // This box now shows the date it applied; so does the other one if its text was rewritten.
    this.setBox(which, 'ok');
    const other = which === 'start' ? 'end' : 'start';
    if ((other === 'start' ? this.startText : this.endText) !== before[other]) {
      this.setBox(other, 'ok');
      this.appliedText[other] = ''; // what was typed there earlier no longer stands
    }
    this.build();
  }

  /**
   * Card #219: add the slashes while digits are typed; once a full day/month/year
   * is in, apply it without waiting for the box to lose focus.
   */
  maskTyping(which: 'start' | 'end', input: HTMLInputElement, next?: HTMLInputElement, event?: Event): void {
    // A keyboard still composing a character (some phone keyboards) must not be
    // interrupted; compositionend calls this again once the character is in.
    if ((event as InputEvent | undefined)?.isComposing) return;
    // Deleting is never re-formatted, so Backspace can remove a slash mid-date.
    const deleting = !!(event as InputEvent | undefined)?.inputType?.startsWith('delete');
    const masked = deleting ? input.value : maskDateDigits(input.value);
    // input + compositionend can both report the same finished date. The second
    // report must change nothing - not even cancel the jump the first one scheduled.
    if (masked === this.appliedText[which] && masked === input.value && input.selectionStart === input.value.length) return;
    if (masked !== input.value) {
      // Keep the cursor where it was relative to the end, so editing mid-date doesn't jump it.
      const fromEnd = input.value.length - (input.selectionStart ?? input.value.length);
      input.value = masked;
      const pos = Math.max(0, masked.length - fromEnd);
      input.setSelectionRange(pos, pos);
    }
    // Any keystroke cancels a pending jump to End: the person is still typing here.
    const keystroke = ++this.keystrokes;
    this.clearFocusTimer();
    // Flag an impossible part as soon as it is typed (a 40th day, a 16th month),
    // saying what is wrong; fixing it clears the flag on the next keystroke.
    const problem = typedDateProblem(masked);
    if (problem) {
      this.setBox(which, 'bad', problem);
      this.appliedText[which] = '';
      return;
    }
    if (!isCompleteTypedDate(masked)) {
      // Part-typed: Apply waits - unless the box is back to the date it showed.
      this.setBox(which, masked === (which === 'start' ? this.startText : this.endText) ? 'ok' : 'editing');
      this.appliedText[which] = '';
      return;
    }
    // Only apply while typing at the end. A digit fixed mid-date can pass through
    // an in-between date (01/11 -> 01/1) that must not replace the range; leaving
    // the box applies the finished edit (blurApply).
    const atEnd = input.selectionStart === input.value.length;
    if (!atEnd) {
      this.setBox(which, 'editing');
      return;
    }
    // Some phone keyboards finish the character only after the box lost focus;
    // then the person is already clicking elsewhere, so don't move the calendar.
    const focused = document.activeElement === input;
    this.typed(which, masked, focused);
    // Remember it only if it was accepted, so leaving the box re-checks a rejected date.
    this.appliedText[which] = this.box[which] === 'bad' ? '' : masked;
    // A valid start moves the cursor on, so both dates can be typed in one go.
    if (this.box[which] !== 'bad' && next && focused) {
      this.focusTimer = setTimeout(() => {
        this.focusTimer = null;
        // Re-check: no newer keystroke, and the person hasn't clicked elsewhere.
        if (keystroke === this.keystrokes && next.isConnected && document.activeElement === input) next.focus();
      });
    }
  }

  /** Leaving a box applies what was typed - unless typing already applied exactly that. */
  blurApply(which: 'start' | 'end', value: string): void {
    const current = which === 'start' ? this.startText : this.endText;
    if (value === current) {
      if (this.box[which] === 'editing') this.setBox(which, 'ok'); // edited back to what it showed
      return;
    }
    // An emptied box is unfinished, not "unrecognised" - and never a reason to apply the old date.
    if (!value.trim()) {
      this.setBox(which, 'editing');
      return;
    }
    if (value === this.appliedText[which]) return;
    this.typed(which, value, false);
  }

  /** Set one box's state; a 'bad' box keeps its message in words, worked out once. */
  private setBox(which: 'start' | 'end', state: 'ok' | 'editing' | 'bad', problem: TypedDateProblem | null = null): void {
    this.box[which] = state;
    this.problemTexts[which] = state === 'bad' && problem ? this.problemText(problem) : null;
  }

  private resetBoxes(): void {
    this.box = { start: 'ok', end: 'ok' };
    this.problemTexts = { start: null, end: null };
  }

  /** Card #219: jump straight to a month and year from a month's header. */
  jumpTo(index: number, year: number | string, month: number | string, select?: HTMLSelectElement): void {
    const y = Number(year);
    const m = Number(month);
    const valid = year !== '' && month !== '' && year != null && month != null &&
      Number.isInteger(y) && y >= MIN_YEAR && y <= MAX_YEAR && Number.isInteger(m) && m >= 0 && m <= 11;
    if (!valid) {
      // Put the changed select back on what its calendar shows (Angular won't: its values didn't change).
      const shown = this.months[index];
      if (select && shown) select.value = String(select.value === String(year) ? shown.year : shown.month);
      return;
    }
    // The picked month lands in the calendar that was changed; the other one stays
    // put (Sep 2016 -> Oct 2026 is one range), unless the two would cross.
    this.calendars = placeCalendar(this.calendars, index === 0 ? 0 : 1, { year: y, month: m });
    this.build();
  }

  ngOnDestroy(): void {
    this.clearFocusTimer();
  }

  private clearFocusTimer(): void {
    if (this.focusTimer) clearTimeout(this.focusTimer);
    this.focusTimer = null;
  }

  /** An arrow on calendar `index` (0 = left, 1 = right). */
  step(index: number, delta: number): void {
    this.calendars = stepCalendar(this.calendars, index === 0 ? 0 : 1, delta);
    this.build();
  }

  setHover(d: Date | null): void {
    if (this.setting === 'end' && this.draftStart) this.hover = d;
  }

  /** Classes for one day cell: range edges, inside, hover preview, today. */
  dayClass(d: Date): Record<string, boolean> {
    const lo = this.draftStart;
    const hi = this.setting === 'end' && this.hover ? this.hover : this.draftEnd;
    // Card #358: the edge/inside classes are shared with the booking date/time picker.
    // An end (or hover) without a start marks nothing here, as before.
    const range = rangeDayClasses(d, lo, lo ? hi : null);
    return {
      'is-today': sameDay(d, this.today),
      'is-future': d > this.today,
      ...range,
      preview: range['in-range'] && this.setting === 'end' && !!this.hover,
    };
  }

  formatDate(d: Date): string {
    const lang = this.lang();
    if (!this.dateFormat || this.dateFormatLang !== lang) {
      // Built once per language: formatDate runs for every day cell on every change detection.
      this.dateFormat = new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric', year: 'numeric' });
      this.dateFormatLang = lang;
    }
    return this.dateFormat.format(d);
  }

  trackCell(i: number, c: CalendarCell): number {
    return c.date ? c.date.getTime() : -i - 1;
  }

  private formatRange(a: Date, b: Date): string {
    return `${this.formatDate(a)} – ${this.formatDate(b)}`;
  }

  private syncText(): void {
    this.startText = this.draftStart ? this.formatDate(this.draftStart) : '';
    this.endText = this.draftEnd ? this.formatDate(this.draftEnd) : '';
  }

  /** Write the box texts into the inputs even when the bound value didn't change. */
  private paintBoxes(): void {
    if (this.startBox) this.startBox.nativeElement.value = this.startText;
    if (this.endBoxRef) this.endBoxRef.nativeElement.value = this.endText;
  }

  private build(): void {
    const lang = this.lang();
    const title = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' });
    this.months = this.calendars.map(({ year, month }) =>
      ({ year, month, title: title.format(new Date(year, month, 1)), cells: monthCells(year, month, this.weekStartsOn) }));
    this.monthLabels = this.monthNames('long');
    this.years = yearOptions(this.today.getFullYear(), ...this.months.map(m => m.year));
    const wd = new Intl.DateTimeFormat(lang, { weekday: 'short' });
    // Jan 4, 2026 is a Sunday.
    this.weekdays = Array.from({ length: 7 }, (_, i) => wd.format(new Date(2026, 0, 4 + ((i + this.weekStartsOn) % 7))).slice(0, 2));
  }

  /** Card #213: say what is wrong with a typed date, in the app's language. */
  private problemText(p: TypedDateProblem): string {
    switch (p.kind) {
      case 'day':
        return this.t('DATE_RANGE.BAD_DAY', 'The day must be between 1 and 31.');
      case 'month':
        return this.t('DATE_RANGE.BAD_MONTH', 'The month must be between 1 and 12.');
      case 'year':
        return this.t('DATE_RANGE.BAD_YEAR', 'The year must be between {{min}} and {{max}}.', { min: MIN_YEAR, max: MAX_YEAR });
      case 'dayInMonth': {
        const name = new Intl.DateTimeFormat(this.lang(), { month: 'long' }).format(new Date(p.year, p.month, 1));
        const month = name.charAt(0).toLocaleUpperCase(this.lang()) + name.slice(1);
        return this.t('DATE_RANGE.BAD_DAY_IN_MONTH', '{{month}} {{year}} has {{days}} days.', { month, year: p.year, days: p.days });
      }
    }
  }

  private monthNames(style: 'short' | 'long'): string[] {
    const f = new Intl.DateTimeFormat(this.lang(), { month: style });
    return Array.from({ length: 12 }, (_, m) => f.format(new Date(2026, m, 1)));
  }

  private lang(): string {
    const l = this.translate.currentLang || this.translate.getDefaultLang() || 'en';
    if (l === this.langChecked) return this.langResolved;
    let resolved = 'en';
    try {
      resolved = Intl.DateTimeFormat.supportedLocalesOf([l]).length ? l : 'en';
    } catch {
      resolved = 'en';
    }
    this.langChecked = l;
    this.langResolved = resolved;
    return resolved;
  }

  /** Translate with a readable English fallback when the key is missing. */
  private t(key: string, fallback: string, params?: Record<string, unknown>): string {
    const v = this.translate.instant(key, params);
    if (v && v !== key) return v;
    return fallback.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => String(params?.[k] ?? ''));
  }
}
